import {
  subscribeToLots,
  type LotSubscriptionTarget,
  type PublicLotRow,
} from '@auctisync/api-client';
import { useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';

export type { LotSubscriptionTarget };

export interface LotSubscriptionOptions {
  target: LotSubscriptionTarget;
  onUpdate: (lot: PublicLotRow) => void;
  /** Poll interval used only while the channel is down. Default 3000 ms (the shared default). */
  disconnectedPollMs?: number;
  enabled?: boolean;
}

/**
 * Realtime for a sale on the SUPABASE BOOK — the parachute (memory:
 * feedback_engine_is_the_default_supabase_is_the_parachute). A sale the
 * engine runs is live through `useSaleFeed`; `useLiveSource` says which.
 *
 * 🔴 NO LONGER A COPY. This file held the website's own copy of
 * `subscribeToLots` — the channel, the retry with back-off, the poll while
 * disconnected, the de-duplication stamp — beside the shared one the app
 * uses (the house rule forbids it: memory feedback_no_mechanical_divergence).
 * It is now only the React lifecycle around the shared function.
 *
 * Returns `isLive` — true while the Realtime channel is subscribed.
 */
export function useLotSubscription({
  target,
  onUpdate,
  disconnectedPollMs,
  enabled = true,
}: LotSubscriptionOptions): { isLive: boolean } {
  const [isLive, setIsLive] = useState(false);
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;

  // Primitives, so a fresh `target` object each render does not resubscribe.
  const kind = target.kind;
  const key =
    target.kind === 'lot'
      ? target.lotId
      : target.kind === 'auction'
        ? target.auctionId
        : target.kind === 'tenant'
          ? target.tenantId
          : 'all';

  useEffect(() => {
    if (!enabled || key === '') return;
    const sub = subscribeToLots(supabase, {
      target: rebuild(kind, key),
      onUpdate: (row) => onUpdateRef.current(row),
      onLiveChange: setIsLive,
      ...(disconnectedPollMs !== undefined ? { disconnectedPollMs } : {}),
    });
    return () => {
      sub.close();
    };
  }, [enabled, kind, key, disconnectedPollMs]);

  return { isLive };
}

function rebuild(kind: LotSubscriptionTarget['kind'], key: string): LotSubscriptionTarget {
  switch (kind) {
    case 'lot':
      return { kind, lotId: key };
    case 'auction':
      return { kind, auctionId: key };
    case 'tenant':
      return { kind, tenantId: key };
    default:
      return { kind: 'public' };
  }
}
